import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { carrierApi, type FleetFinanceAccount, type FleetFinancePayment } from '../lib/api'

type FinanceFormState = {
  unitId: string
  financeType: string
  ownershipType: string
  lenderName: string
  lessorName: string
  lienholderName: string
  contractNumber: string
  accountNumberLast4: string
  originalAmount: string
  currentBalance: string
  paymentAmount: string
  paymentFrequency: string
  nextDueDate: string
  maturityDate: string
  status: string
  notes: string
}

type PaymentFormState = {
  paymentDate: string
  amount: string
  paymentMethod: string
  notes: string
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

function centsToMoney(value?: number | null) {
  if (value == null) return '—'
  return money.format(value / 100)
}

function today() {
  return new Date().toISOString().slice(0, 10)
}

function buildFinanceForm(account?: FleetFinanceAccount | null, unitId?: string): FinanceFormState {
  return {
    unitId: unitId ?? account?.equipmentUnitId ?? '',
    financeType: account?.financeType ?? 'loan',
    ownershipType: account?.ownershipType ?? 'leased_to_carrier',
    lenderName: account?.lenderName ?? '',
    lessorName: account?.lessorName ?? '',
    lienholderName: account?.lienholderName ?? '',
    contractNumber: account?.contractNumber ?? '',
    accountNumberLast4: account?.accountNumberLast4 ?? '',
    originalAmount: account?.originalAmountCents != null ? String(account.originalAmountCents) : '',
    currentBalance: account?.currentBalanceCents != null ? String(account.currentBalanceCents) : '',
    paymentAmount: account?.paymentAmountCents != null ? String(account.paymentAmountCents) : '',
    paymentFrequency: account?.paymentFrequency ?? 'monthly',
    nextDueDate: account?.nextDueDate ?? '',
    maturityDate: account?.maturityDate ?? '',
    status: account?.status ?? 'active',
    notes: account?.notes ?? '',
  }
}

function buildPaymentForm(account?: FleetFinanceAccount | null): PaymentFormState {
  return {
    paymentDate: today(),
    amount: account?.paymentAmountCents != null ? String(account.paymentAmountCents) : '',
    paymentMethod: 'manual',
    notes: '',
  }
}

function formatUnitLabel(unit?: any) {
  return [unit?.unitNumber, unit?.displayName].filter(Boolean).join(' - ') || unit?.id || 'Unknown unit'
}

export default function CarrierFleetFinancePage() {
  const qc = useQueryClient()
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const [financeForm, setFinanceForm] = useState<FinanceFormState>(() => buildFinanceForm())
  const [paymentForm, setPaymentForm] = useState<PaymentFormState>(() => buildPaymentForm())

  const overviewQ = useQuery({
    queryKey: ['fleet-finance-overview'],
    queryFn: () => carrierApi.getFinanceOverview().then(r => r.data),
  })

  const accountsQ = useQuery({
    queryKey: ['fleet-finance-accounts'],
    queryFn: () => carrierApi.listFinanceAccounts().then(r => r.data.accounts),
  })

  const unitsQ = useQuery({
    queryKey: ['carrier-equipment-units-for-finance'],
    queryFn: () => carrierApi.listEquipment().then(r => r.data.units),
  })

  const accounts = accountsQ.data ?? []
  const selectedAccount = useMemo(
    () => accounts.find(account => account.id === selectedAccountId) ?? accounts[0] ?? null,
    [accounts, selectedAccountId]
  )
  const activeUnits = (unitsQ.data ?? []).filter((unit: any) => String(unit.unitStatus ?? '').toUpperCase() !== 'RETIRED')

  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      setSelectedAccountId(accounts[0].id)
    }
    if (selectedAccountId && !accounts.some(account => account.id === selectedAccountId) && accounts.length > 0) {
      setSelectedAccountId(accounts[0].id)
    }
  }, [accounts, selectedAccountId])

  useEffect(() => {
    if (selectedAccount) {
      setFinanceForm(buildFinanceForm(selectedAccount, selectedAccount.equipmentUnitId))
      setPaymentForm(buildPaymentForm(selectedAccount))
    }
  }, [selectedAccount])

  useEffect(() => {
    if (!financeForm.unitId && activeUnits[0]?.id) {
      setFinanceForm(prev => ({ ...prev, unitId: activeUnits[0].id }))
    }
  }, [activeUnits, financeForm.unitId])

  const saveAccountMut = useMutation({
    mutationFn: async () => {
      if (!financeForm.unitId) throw new Error('Select an equipment unit first')
      const payload = {
        financeType: financeForm.financeType || 'unknown',
        ownershipType: financeForm.ownershipType || 'unknown',
        lenderName: financeForm.lenderName || undefined,
        lessorName: financeForm.lessorName || undefined,
        lienholderName: financeForm.lienholderName || undefined,
        contractNumber: financeForm.contractNumber || undefined,
        accountNumberLast4: financeForm.accountNumberLast4 || undefined,
        originalAmountCents: financeForm.originalAmount ? Number(financeForm.originalAmount) : undefined,
        currentBalanceCents: financeForm.currentBalance ? Number(financeForm.currentBalance) : undefined,
        paymentAmountCents: financeForm.paymentAmount ? Number(financeForm.paymentAmount) : undefined,
        paymentFrequency: financeForm.paymentFrequency || undefined,
        nextDueDate: financeForm.nextDueDate || undefined,
        maturityDate: financeForm.maturityDate || undefined,
        status: financeForm.status || undefined,
        notes: financeForm.notes || undefined,
      }

      return selectedAccount
        ? carrierApi.updateUnitFinance(financeForm.unitId, selectedAccount.id, payload)
        : carrierApi.createUnitFinance(financeForm.unitId, payload)
    },
    onSuccess: async response => {
      const account = (response as any).data?.account ?? (response as any).data?.account ?? null
      await qc.invalidateQueries({ queryKey: ['fleet-finance-accounts'] })
      await qc.invalidateQueries({ queryKey: ['fleet-finance-overview'] })
      if (account?.id) {
        setSelectedAccountId(account.id)
      }
    },
  })

  const paymentHistoryQ = useQuery({
    queryKey: ['fleet-finance-payments', selectedAccount?.equipmentUnitId, selectedAccount?.id],
    queryFn: () => carrierApi.listFinancePayments(selectedAccount!.equipmentUnitId, selectedAccount!.id).then(r => r.data.payments),
    enabled: !!selectedAccount,
  })

  const savePaymentMut = useMutation({
    mutationFn: async () => {
      if (!selectedAccount) throw new Error('Select an account first')
      if (!paymentForm.paymentDate || !paymentForm.amount) throw new Error('Payment date and amount are required')
      return carrierApi.createFinancePayment(selectedAccount.equipmentUnitId, selectedAccount.id, {
        paymentDate: paymentForm.paymentDate,
        amountCents: Number(paymentForm.amount),
        paymentMethod: paymentForm.paymentMethod || 'manual',
        notes: paymentForm.notes || undefined,
      })
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['fleet-finance-accounts'] })
      await qc.invalidateQueries({ queryKey: ['fleet-finance-overview'] })
      await qc.invalidateQueries({ queryKey: ['fleet-finance-payments', selectedAccount?.equipmentUnitId, selectedAccount?.id] })
      setPaymentForm(prev => ({ ...prev, paymentDate: today(), notes: '' }))
    },
  })

  const accountsSorted = [...accounts].sort((a, b) => {
    const aDue = a.nextDueDate ?? '9999-12-31'
    const bDue = b.nextDueDate ?? '9999-12-31'
    return aDue.localeCompare(bDue) || (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
  })

  const overview = overviewQ.data ?? {
    monthlyPaymentTotalCents: 0,
    weeklyPaymentTotalCents: 0,
    activeLoanCount: 0,
    activeLeaseCount: 0,
    pastDueCount: 0,
    dueSoonCount: 0,
    totalCurrentBalanceCents: 0,
    warnings: [],
  }

  const warnings = overview.warnings ?? []
  const payments = paymentHistoryQ.data ?? []

  return (
    <div className="container mx-auto max-w-6xl p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fleet Finance</h1>
          <p className="mt-1 text-sm text-gray-500">Carrier and internal tracking for leased and financed equipment. No money movement happens here.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/carrier/equipment" className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Back to Equipment</Link>
          <Link to="/carrier/dashboard" className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Carrier Dashboard</Link>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 md:grid-cols-3">
        <div>
          <div className="font-semibold">Tracking only</div>
          <div>Payment records, payoff notes, and document tracking only. No payment rail is connected.</div>
        </div>
        <div>
          <div className="font-semibold">Driver blocked</div>
          <div>Drivers do not see this page or the Fleet Finance links.</div>
        </div>
        <div>
          <div className="font-semibold">Broker blocked</div>
          <div>Broker and shipper roles are excluded by route guard and nav grouping.</div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Monthly payments" value={centsToMoney(overview.monthlyPaymentTotalCents)} />
        <MetricCard label="Weekly payments" value={centsToMoney(overview.weeklyPaymentTotalCents)} />
        <MetricCard label="Active loans" value={String(overview.activeLoanCount)} />
        <MetricCard label="Active leases" value={String(overview.activeLeaseCount)} />
        <MetricCard label="Past due" value={String(overview.pastDueCount)} tone={overview.pastDueCount > 0 ? 'text-red-700' : 'text-gray-900'} />
        <MetricCard label="Due soon" value={String(overview.dueSoonCount)} tone={overview.dueSoonCount > 0 ? 'text-amber-700' : 'text-gray-900'} />
        <MetricCard label="Balance" value={centsToMoney(overview.totalCurrentBalanceCents)} />
        <MetricCard label="Warnings" value={String(warnings.length)} tone={warnings.length > 0 ? 'text-amber-700' : 'text-gray-900'} />
      </div>

      {warnings.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-800">Overview warnings</h2>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-amber-900">
            {warnings.map((warning: string) => (
              <span key={warning} className="rounded-full bg-amber-100 px-3 py-1">{warning}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.25fr_0.9fr]">
        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Active leases and loans</h2>
                <p className="text-sm text-gray-500">Select an account to review payment history or update tracking details.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedAccountId('')
                  setFinanceForm(buildFinanceForm(null, activeUnits[0]?.id))
                }}
                className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
              >
                New account
              </button>
            </div>

            <div className="mt-4 grid gap-3">
              {accountsSorted.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 p-6 text-sm text-gray-500">
                  No finance accounts yet. Use the form below to create the first tracking record.
                </div>
              ) : accountsSorted.map(account => {
                const isSelected = account.id === selectedAccount?.id
                const unit = account.unit
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => setSelectedAccountId(account.id)}
                    className={`rounded-xl border p-4 text-left transition ${isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{formatUnitLabel(unit)}</div>
                        <div className="text-xs text-gray-500">{account.financeType ?? 'unknown'} · {account.ownershipType ?? 'unknown'}</div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>{account.status ?? 'unknown'}</div>
                        <div>{account.nextDueDate ? `Due ${account.nextDueDate}` : 'No due date'}</div>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-3 text-sm">
                      <InfoPill label="Balance" value={centsToMoney(account.currentBalanceCents)} />
                      <InfoPill label="Payment" value={centsToMoney(account.paymentAmountCents)} />
                      <InfoPill label="Warnings" value={String(account.warnings?.length ?? 0)} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">{account.docCount ?? 0} docs</span>
                      {account.payoffGoodThrough && <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">Payoff good through {account.payoffGoodThrough}</span>}
                      {account.maturityDate && <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">Matures {account.maturityDate}</span>}
                    </div>
                  </button>
                )
              })}
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Create or edit finance account</h2>
                <p className="text-sm text-gray-500">Tracking fields only. Use this to keep leases and loans current.</p>
              </div>
              {selectedAccount && (
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">Editing existing account</span>
              )}
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <SelectField label="Equipment unit" value={financeForm.unitId} onChange={value => setFinanceForm(prev => ({ ...prev, unitId: value }))} options={activeUnits.map((unit: any) => ({ value: unit.id, label: formatUnitLabel(unit) }))} />
              <SelectField label="Finance type" value={financeForm.financeType} onChange={value => setFinanceForm(prev => ({ ...prev, financeType: value }))} options={[
                { value: 'loan', label: 'Loan' },
                { value: 'lease', label: 'Lease' },
                { value: 'lease_purchase', label: 'Lease purchase' },
                { value: 'rental', label: 'Rental' },
                { value: 'owner_operator', label: 'Owner-operator' },
                { value: 'unknown', label: 'Unknown' },
              ]} />
              <SelectField label="Ownership type" value={financeForm.ownershipType} onChange={value => setFinanceForm(prev => ({ ...prev, ownershipType: value }))} options={[
                { value: 'company_owned', label: 'Company owned' },
                { value: 'owner_operator_owned', label: 'Owner-operator owned' },
                { value: 'leased_to_carrier', label: 'Leased to carrier' },
                { value: 'rented', label: 'Rented' },
                { value: 'unknown', label: 'Unknown' },
              ]} />
              <TextField label="Lender" value={financeForm.lenderName} onChange={value => setFinanceForm(prev => ({ ...prev, lenderName: value }))} />
              <TextField label="Lessor" value={financeForm.lessorName} onChange={value => setFinanceForm(prev => ({ ...prev, lessorName: value }))} />
              <TextField label="Lienholder" value={financeForm.lienholderName} onChange={value => setFinanceForm(prev => ({ ...prev, lienholderName: value }))} />
              <TextField label="Contract number" value={financeForm.contractNumber} onChange={value => setFinanceForm(prev => ({ ...prev, contractNumber: value }))} />
              <TextField label="Account last 4" value={financeForm.accountNumberLast4} onChange={value => setFinanceForm(prev => ({ ...prev, accountNumberLast4: value }))} />
              <TextField label="Original amount (cents)" value={financeForm.originalAmount} onChange={value => setFinanceForm(prev => ({ ...prev, originalAmount: value }))} />
              <TextField label="Current balance (cents)" value={financeForm.currentBalance} onChange={value => setFinanceForm(prev => ({ ...prev, currentBalance: value }))} />
              <TextField label="Payment amount (cents)" value={financeForm.paymentAmount} onChange={value => setFinanceForm(prev => ({ ...prev, paymentAmount: value }))} />
              <SelectField label="Payment frequency" value={financeForm.paymentFrequency} onChange={value => setFinanceForm(prev => ({ ...prev, paymentFrequency: value }))} options={[
                { value: 'weekly', label: 'Weekly' },
                { value: 'biweekly', label: 'Biweekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'annual', label: 'Annual' },
                { value: 'custom', label: 'Custom' },
              ]} />
              <TextField label="Next due date" type="date" value={financeForm.nextDueDate} onChange={value => setFinanceForm(prev => ({ ...prev, nextDueDate: value }))} />
              <TextField label="Maturity date" type="date" value={financeForm.maturityDate} onChange={value => setFinanceForm(prev => ({ ...prev, maturityDate: value }))} />
              <SelectField label="Status" value={financeForm.status} onChange={value => setFinanceForm(prev => ({ ...prev, status: value }))} options={[
                { value: 'active', label: 'Active' },
                { value: 'current', label: 'Current' },
                { value: 'past_due', label: 'Past due' },
                { value: 'paid_off', label: 'Paid off' },
                { value: 'defaulted', label: 'Defaulted' },
                { value: 'closed', label: 'Closed' },
                { value: 'unknown', label: 'Unknown' },
              ]} />
            </div>

            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={financeForm.notes}
                onChange={e => setFinanceForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                placeholder="Tracking notes only. No real funds move here."
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => saveAccountMut.mutate()}
                disabled={saveAccountMut.isPending || !financeForm.unitId}
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saveAccountMut.isPending ? 'Saving…' : selectedAccount ? 'Save changes' : 'Create account'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedAccountId('')
                  setFinanceForm(buildFinanceForm(null, activeUnits[0]?.id))
                  setPaymentForm(buildPaymentForm())
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="text-lg font-semibold text-gray-900">Payment history</h2>
            <p className="text-sm text-gray-500">Record payments as tracking events only. This does not move cash.</p>

            {selectedAccount ? (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
                  <div className="font-medium text-gray-900">{formatUnitLabel(selectedAccount.unit)}</div>
                  <div className="text-xs text-gray-500">Finance account {selectedAccount.id}</div>
                </div>

                <div className="grid gap-3">
                  <TextField label="Payment date" type="date" value={paymentForm.paymentDate} onChange={value => setPaymentForm(prev => ({ ...prev, paymentDate: value }))} />
                  <TextField label="Amount (cents)" value={paymentForm.amount} onChange={value => setPaymentForm(prev => ({ ...prev, amount: value }))} />
                  <SelectField label="Payment method" value={paymentForm.paymentMethod} onChange={value => setPaymentForm(prev => ({ ...prev, paymentMethod: value }))} options={[
                    { value: 'manual', label: 'Manual' },
                    { value: 'ach', label: 'ACH' },
                    { value: 'card', label: 'Card' },
                    { value: 'check', label: 'Check' },
                    { value: 'cash', label: 'Cash' },
                    { value: 'settlement_deduction', label: 'Settlement deduction' },
                    { value: 'external', label: 'External' },
                    { value: 'other', label: 'Other' },
                  ]} />
                  <TextField label="Notes" value={paymentForm.notes} onChange={value => setPaymentForm(prev => ({ ...prev, notes: value }))} />
                </div>

                <button
                  type="button"
                  onClick={() => savePaymentMut.mutate()}
                  disabled={savePaymentMut.isPending}
                  className="w-full rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savePaymentMut.isPending ? 'Recording…' : 'Record payment'}
                </button>

                <div className="rounded-xl border border-gray-200">
                  <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-900">Recent payments</div>
                  <div className="divide-y divide-gray-100">
                    {paymentHistoryQ.isLoading && <div className="px-4 py-4 text-sm text-gray-500">Loading payments…</div>}
                    {!paymentHistoryQ.isLoading && payments.length === 0 && <div className="px-4 py-4 text-sm text-gray-500">No payments recorded yet.</div>}
                    {payments.map((payment: FleetFinancePayment) => (
                      <div key={payment.id} className="px-4 py-3 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-medium text-gray-900">{centsToMoney(payment.amountCents)}</div>
                            <div className="text-xs text-gray-500">{payment.paymentDate} · {payment.paymentMethod ?? 'manual'} · {payment.status ?? 'recorded'}</div>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            {payment.referenceNumber && <div>Ref {payment.referenceNumber}</div>}
                            {payment.externalPaymentId && <div>External {payment.externalPaymentId}</div>}
                          </div>
                        </div>
                        {payment.notes && <div className="mt-2 text-xs text-gray-600">{payment.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                Select an account to view payment history.
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="text-lg font-semibold text-gray-900">Document tracking</h2>
            <p className="mt-1 text-sm text-gray-500">The backend stores document metadata here; the UI keeps this as a tracking surface only.</p>
            <div className="mt-4 space-y-2 text-sm text-gray-600">
              <div className="rounded-lg bg-gray-50 px-3 py-2">Finance account documents, payoff letters, and lien release metadata remain tracking only.</div>
              <div className="rounded-lg bg-gray-50 px-3 py-2">No settlement or payment transfer occurs from this page.</div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

function MetricCard({ label, value, tone = 'text-gray-900' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-2 text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  )
}

function InfoPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900">{value}</div>
    </div>
  )
}

function TextField({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
      />
    </label>
  )
}

function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  )
}
